import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { COLOR_PRESETS, DEFAULT_PRESET, resolveColors } from '../../lib/colorTheme';

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

const PRESET_OPTIONS = [
  { value: 'primavera', label: 'Primavera (P6 Standard)' },
  { value: 'epic_plan', label: 'epicplan' },
  { value: 'custom', label: 'Custom' },
];

const FIELDS = [
  { key: 'critical', label: 'Critical Path' },
  { key: 'completed', label: 'Completed' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'not_started', label: 'Not Started' },
];

// Swatch row used to preview each preset's 4 colors
const Swatches = ({ colors }) => (
  <div className="flex items-center gap-1">
    {FIELDS.map((f) => (
      <span
        key={f.key}
        className="inline-block h-4 w-4 rounded border border-slate-200"
        style={{ backgroundColor: colors[f.key] }}
        title={f.label}
        data-testid={`swatch-${f.key}`}
      />
    ))}
  </div>
);

const AppearanceSettings = ({ value, onChange, disabled = false }) => {
  // Derive the current preset name (falling back to default if absent)
  const currentPreset = value?.preset && PRESET_OPTIONS.some((o) => o.value === value.preset)
    ? value.preset
    : DEFAULT_PRESET;

  // Local state for the custom palette — kept even when the user toggles
  // off custom so their work isn't lost when they flip back.
  const [customDraft, setCustomDraft] = useState(() => {
    if (value?.preset === 'custom' && value?.custom_colors) {
      return { ...value.custom_colors };
    }
    // Seed from whatever the resolved preset currently is so the user has
    // sensible starting colors when they first click "Custom".
    return resolveColors(value);
  });

  // Keep draft in sync when the parent's value changes to a custom scheme
  // (e.g. loading a different project).
  useEffect(() => {
    if (value?.preset === 'custom' && value?.custom_colors) {
      setCustomDraft({ ...value.custom_colors });
    }
  }, [value]);

  // Per-field hex text input state, so the user can type partial hex values
  // without clobbering the parent state until a full valid value exists.
  const [hexInputs, setHexInputs] = useState(() => ({ ...customDraft }));

  useEffect(() => {
    setHexInputs({ ...customDraft });
  }, [customDraft]);

  const previewColors = useMemo(() => {
    if (currentPreset === 'custom') return customDraft;
    return COLOR_PRESETS[currentPreset] || COLOR_PRESETS[DEFAULT_PRESET];
  }, [currentPreset, customDraft]);

  // Debounce only the color-picker drag path. The OS color picker fires
  // a change event on every hue tick while the user drags, which would
  // otherwise flood the parent with PUTs and toasts.
  const pendingSaveRef = useRef(null);

  useEffect(() => () => {
    if (pendingSaveRef.current) clearTimeout(pendingSaveRef.current);
  }, []);

  const flushPending = () => {
    if (pendingSaveRef.current) {
      clearTimeout(pendingSaveRef.current);
      pendingSaveRef.current = null;
    }
  };

  const scheduleOnChange = (scheme) => {
    flushPending();
    pendingSaveRef.current = setTimeout(() => {
      pendingSaveRef.current = null;
      onChange(scheme);
    }, 400);
  };

  const handlePresetChange = (nextPreset) => {
    if (disabled) return;
    flushPending();
    if (nextPreset === 'custom') {
      onChange({ preset: 'custom', custom_colors: { ...customDraft } });
    } else {
      // Preserve custom_colors null per contract when not on custom
      onChange({ preset: nextPreset, custom_colors: null });
    }
  };

  const commitCustomColor = (key, hex, { debounce = false } = {}) => {
    const next = { ...customDraft, [key]: hex };
    setCustomDraft(next);
    const scheme = { preset: 'custom', custom_colors: next };
    if (debounce) {
      scheduleOnChange(scheme);
    } else {
      flushPending();
      onChange(scheme);
    }
  };

  const handleColorPickerChange = (key, e) => {
    if (disabled) return;
    const hex = e.target.value.toUpperCase();
    commitCustomColor(key, hex, { debounce: true });
  };

  const handleHexTextChange = (key, e) => {
    if (disabled) return;
    const raw = e.target.value;
    setHexInputs((prev) => ({ ...prev, [key]: raw }));
    // Only propagate to parent when we have a full valid hex
    if (HEX_RE.test(raw)) {
      commitCustomColor(key, raw.toUpperCase());
    }
  };

  return (
    <Card className="border-slate-200" data-testid="appearance-settings">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-slate-900">
          Schedule Color Scheme
        </CardTitle>
        <CardDescription className="text-sm text-slate-500">
          Choose the color palette used for Gantt bars, dependency arrows, and
          legends across this project's schedules.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <RadioGroup
          value={currentPreset}
          onValueChange={handlePresetChange}
          disabled={disabled}
          className="space-y-2"
        >
          {PRESET_OPTIONS.map((opt) => {
            const previewTable =
              opt.value === 'custom' ? customDraft : COLOR_PRESETS[opt.value];
            return (
              <label
                key={opt.value}
                htmlFor={`preset-${opt.value}`}
                className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50"
                data-testid={`preset-option-${opt.value}`}
              >
                <div className="flex items-center gap-3">
                  <RadioGroupItem
                    id={`preset-${opt.value}`}
                    value={opt.value}
                    disabled={disabled}
                  />
                  <span className="text-sm font-medium text-slate-900">
                    {opt.label}
                  </span>
                </div>
                {previewTable && <Swatches colors={previewTable} />}
              </label>
            );
          })}
        </RadioGroup>

        {currentPreset === 'custom' && (
          <div
            className="grid grid-cols-1 gap-3 pt-2 border-t border-slate-200"
            data-testid="custom-colors-grid"
          >
            {FIELDS.map((f) => (
              <div
                key={f.key}
                className="grid grid-cols-[1fr_auto_120px] items-center gap-3"
              >
                <Label
                  htmlFor={`color-hex-${f.key}`}
                  className="text-sm font-medium text-slate-900"
                >
                  {f.label}
                </Label>
                <input
                  type="color"
                  value={customDraft[f.key] || '#000000'}
                  onChange={(e) => handleColorPickerChange(f.key, e)}
                  disabled={disabled}
                  className="h-9 w-12 rounded border border-slate-200 bg-white cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid={`color-picker-${f.key}`}
                />
                <Input
                  id={`color-hex-${f.key}`}
                  value={hexInputs[f.key] || ''}
                  onChange={(e) => handleHexTextChange(f.key, e)}
                  disabled={disabled}
                  placeholder="#RRGGBB"
                  maxLength={7}
                  className="h-9 font-mono text-sm uppercase"
                  data-testid={`color-hex-${f.key}`}
                />
              </div>
            ))}
          </div>
        )}

        {/* Live preview row */}
        <div className="pt-3 border-t border-slate-200">
          <p className="text-xs text-slate-500 mb-2">Preview</p>
          <div className="flex items-center gap-4 flex-wrap">
            {FIELDS.map((f) => (
              <div key={f.key} className="flex items-center gap-1.5">
                <div
                  className="h-3 w-6 rounded"
                  style={{ backgroundColor: previewColors[f.key] }}
                />
                <span className="text-[11px] text-slate-600">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AppearanceSettings;
